import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import Text from "components/common/Text";
import Select from "components/common/Select";
import Button from "components/common/Button";
import { AlertType } from "components/common/Snackbar";

import { showSnackbar } from "store/actions/Utility";
import EditCreditSchema from "validations/EditCreditSchema";
import { editCreditForCustomer } from "services/Admin/Customer";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import { CREDIT_ADJUSTMENT_REASON_DROPDOWN_VALUES } from "constants";

import "./EditCreditModal.scss";

export default function EditCreditModal({ open, setOpen, customerId = "", previousCreditAmount = 0, onCreditUpdated }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      reason: "",
      description: "",
    },
    resolver: yupResolver(EditCreditSchema),
  });

  const { handleSubmit, formState, watch } = methods;
  const { isValid } = formState;
  const selectedReason = watch("reason");

  const handleClose = () => {
    setOpen(false);
    methods.reset();
  };

  const onSubmit = async (payload) => {
    setLoading(true);
    try {
      await editCreditForCustomer(customerId, payload);

      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.success,
          message: "Credit amount updated successfully.",
        })
      );

      setOpen(false);
      methods.reset();
      onCreditUpdated?.();
    } catch (error) {
      const apiError = error?.message || "Something went wrong.";

      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: apiError,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-credit-modal"
      data-testid="edit-credit-modal"
      size="sm"
      title={<DialogTitle title="Adjust Credit" />}
      content={
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              <Text
                startIcon={<AttachMoneyIcon className="color-icon-secondary" />}
                label="Current credit amount"
                value={previousCreditAmount?.toString()}
                disabled
                type="number"
              />
              <div className="mt-1">
                <Text
                  startIcon={<AttachMoneyIcon className="color-icon-secondary" />}
                  name="creditAmount"
                  label="New credit amount"
                  type="number"
                  minValue="0"
                  helperTextWithoutError="Enter the updated credit amount. This change will take effect immediately."
                />
              </div>
              <div className="mt-1">
                <Select
                  label="Adjustment Reason"
                  options={CREDIT_ADJUSTMENT_REASON_DROPDOWN_VALUES}
                  name="reason"
                  placeholder="Select"
                />
              </div>

              {selectedReason === "Other" && (
                <div className="mt-1">
                  <Text name="description" multiline rows={3} label="Please describe adjustment reason" size="medium" />
                  <p className="adjust-credit-max-words">Max 500 characters</p>
                </div>
              )}
            </DialogContent>
          </form>
        </FormProvider>
      }
      actions={
        <DialogActions
          action1={<Button isDisabled={loading} variant="text" label="Cancel" size="large" onClick={handleClose} />}
          action2={
            <Button
              isLoading={loading}
              data-testid="submit-credit-button"
              onClick={handleSubmit(onSubmit)}
              label="Save Changes"
              size="large"
              isDisabled={!isValid}
            />
          }
        />
      }
    />
  );
}
